import type { HTTPFailureTrendSeries } from '@/types/alertAttribution';

export interface HTTPFailureTrendTooltipPayload {
  color?: string;
  dataKey?: string | number;
  value?: string | number;
}

export interface HTTPFailureTrendTooltipRow {
  color?: string;
  count: number;
  key: string;
  label: string;
  percentage: number;
}

export const HTTP_FAILURE_TREND_BUCKET_OPTIONS = [
  { label: '1 分钟', value: 60 },
  { label: '5 分钟', value: 5 * 60 },
  { label: '1 小时', value: 60 * 60 },
] as const;

export function aggregateHTTPFailureTrendSeries(
  series: HTTPFailureTrendSeries[],
  from: string,
  to: string,
  bucketSeconds: number,
): HTTPFailureTrendSeries[] {
  const start = Date.parse(from);
  const end = Date.parse(to);
  const bucketMilliseconds = bucketSeconds * 1000;
  if (
    !Number.isFinite(start) ||
    !Number.isFinite(end) ||
    end <= start ||
    !Number.isFinite(bucketMilliseconds) ||
    bucketMilliseconds <= 0
  ) {
    return series;
  }

  const bucketCount = Math.ceil((end - start) / bucketMilliseconds);
  return series.map((item) => {
    const counts = new Array<number>(bucketCount).fill(0);
    for (const point of item.points) {
      const timestamp = Date.parse(point.timestamp);
      const bucketIndex = Math.floor((timestamp - start) / bucketMilliseconds);
      if (
        Number.isFinite(timestamp) &&
        bucketIndex >= 0 &&
        bucketIndex < bucketCount
      ) {
        counts[bucketIndex] += point.count;
      }
    }
    return {
      ...item,
      points: counts.map((count, index) => ({
        count,
        timestamp: new Date(start + index * bucketMilliseconds).toISOString(),
      })),
    };
  });
}

export function buildHTTPFailureTrendTooltipRows(
  payload: HTTPFailureTrendTooltipPayload[],
  series: HTTPFailureTrendSeries[],
): {
  total: number;
  clusters: HTTPFailureTrendTooltipRow[];
} {
  const seriesByKey = new Map(series.map((item) => [item.key, item]));
  const total = Number(
    payload.find((item) => item.dataKey === 'total')?.value ?? 0,
  );
  const clusters = payload
    .filter((item) => item.dataKey !== 'total' && Number(item.value) > 0)
    .map((item) => ({
      color: item.color,
      count: Number(item.value),
      key: String(item.dataKey || 'UNKNOWN'),
      label:
        seriesByKey.get(String(item.dataKey))?.label ||
        String(item.dataKey || 'UNKNOWN'),
      percentage: total > 0 ? (Number(item.value) / total) * 100 : 0,
    }))
    .sort(
      (left, right) =>
        right.count - left.count || left.label.localeCompare(right.label),
    );
  return { total, clusters };
}
